import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Input, Label } from '@immo/ui-kit';
import { loadGoogleMaps } from './google-maps/load-script';
import { parsePlace, type ParsedAddress } from './google-maps/parse-place';

type StandaloneProps = {
  readonly apiKey: string | undefined;
  readonly label: string;
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly onPlaceSelected: (address: ParsedAddress) => void;
  readonly value?: undefined;
  readonly onValueChange?: undefined;
  readonly id?: undefined;
  readonly invalid?: undefined;
  readonly 'aria-describedby'?: undefined;
};

type ControlledProps = {
  readonly apiKey: string | undefined;
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly onPlaceSelected: (address: ParsedAddress) => void;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly id: string;
  readonly invalid?: boolean;
  readonly 'aria-describedby': string | undefined;
  readonly label?: undefined;
};

type Props = StandaloneProps | ControlledProps;

/*
 * The new Places API exposes Place predictions through
 * `AutocompleteSuggestion.fetchAutocompleteSuggestions`. Going through that
 * API (rather than dropping in `<gmp-place-autocomplete>`) lets us keep our
 * own `<Input>` + token-styled dropdown, matching the rest of the form.
 *
 * Billing-wise we mint one `AutocompleteSessionToken` per user session and
 * rotate it after each selection — Google bills the autocomplete call and
 * the subsequent Place Details fetch as one session when the token matches.
 */

type PlacePrediction = {
  readonly placeId: string;
  readonly text: { toString: () => string };
  readonly toPlace: () => GooglePlace;
};

type Suggestion = { readonly placePrediction: PlacePrediction };

type FetchSuggestionsRequest = {
  readonly input: string;
  readonly sessionToken: unknown;
  readonly includedRegionCodes?: ReadonlyArray<string>;
};

type GooglePlace = Parameters<typeof parsePlace>[0] & {
  readonly fetchFields: (opts: { readonly fields: ReadonlyArray<string> }) => Promise<unknown>;
};

type PlacesLib = {
  readonly AutocompleteSessionToken: new () => unknown;
  readonly AutocompleteSuggestion: {
    readonly fetchAutocompleteSuggestions: (
      req: FetchSuggestionsRequest,
    ) => Promise<{ readonly suggestions: ReadonlyArray<Suggestion> }>;
  };
};

type GoogleNamespace = {
  readonly maps: {
    readonly importLibrary: (name: 'places') => Promise<PlacesLib>;
  };
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

export function AddressAutocomplete(props: Props) {
  const { apiKey, placeholder, disabled, onPlaceSelected } = props;
  const isControlled = props.value !== undefined;
  const [internalQuery, setInternalQuery] = useState('');
  const query = isControlled ? props.value : internalQuery;
  const setQuery = (value: string) => {
    if (isControlled) {
      props.onValueChange(value);
    } else {
      setInternalQuery(value);
    }
  };
  const inputId = isControlled ? props.id : 'address-autocomplete';
  const listboxId = `${inputId}-listbox`;
  const [results, setResults] = useState<ReadonlyArray<Suggestion>>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const libRef = useRef<PlacesLib | null>(null);
  const tokenRef = useRef<unknown>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    if (apiKey === undefined) return;

    let cancelled = false;

    void (async () => {
      try {
        await loadGoogleMaps(apiKey);

        if (cancelled) return;

        const google = (globalThis as unknown as { google?: GoogleNamespace }).google;

        if (google === undefined) return;

        const lib = await google.maps.importLibrary('places');

        if (cancelled) return;

        libRef.current = lib;
        tokenRef.current = new lib.AutocompleteSessionToken();
      } catch (err) {
        console.error('[AddressAutocomplete] failed to load places library', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    const trimmed = query.trim();

    if (libRef.current === null || trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);

      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const lib = libRef.current;

          if (lib === null) return;

          const response = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: trimmed,
            sessionToken: tokenRef.current,
            includedRegionCodes: ['de'],
          });

          if (!cancelled) {
            setResults(response.suggestions);
            setActiveIndex(-1);
          }
        } catch (err) {
          console.error('[AddressAutocomplete] suggestion fetch failed', err);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    if (!open) return;

    const handler = (event: MouseEvent) => {
      if (wrapperRef.current === null) return;

      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);

      return;
    }

    if (results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev + 1) % results.length);

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));

      return;
    }

    if (event.key === 'Enter' && open && activeIndex >= 0) {
      const suggestion = results[activeIndex];

      if (suggestion !== undefined) {
        event.preventDefault();
        void handleSelect(suggestion);
      }
    }
  };

  useEffect(() => {
    if (activeIndex < 0 || listRef.current === null) return;

    const item = listRef.current.children[activeIndex];

    if (item instanceof HTMLElement) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleSelect = async (suggestion: Suggestion) => {
    setOpen(false);
    setQuery(suggestion.placePrediction.text.toString());

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'location'] });
      onPlaceSelectedRef.current(parsePlace(place));
    } catch (err) {
      console.error('[AddressAutocomplete] place fetch failed', err);
    }

    if (libRef.current !== null) {
      tokenRef.current = new libRef.current.AutocompleteSessionToken();
    }
  };

  const showDropdown = open && results.length > 0;

  return (
    <div ref={wrapperRef} className="relative space-y-1">
      {!isControlled && <Label htmlFor={inputId}>{props.label}</Label>}
      <Input
        id={inputId}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        {...(isControlled && props.invalid === true ? { invalid: true } : {})}
        {...(isControlled && props['aria-describedby'] !== undefined
          ? { 'aria-describedby': props['aria-describedby'] }
          : {})}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 && results[activeIndex] !== undefined
            ? `${inputId}-option-${results[activeIndex].placePrediction.placeId}`
            : undefined
        }
      />
      {showDropdown && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[320px] overflow-y-auto rounded-md border border-border bg-surface shadow-md"
        >
          {results.map((suggestion, index) => {
            const isActive = index === activeIndex;

            return (
              <li
                key={suggestion.placePrediction.placeId}
                id={`${inputId}-option-${suggestion.placePrediction.placeId}`}
                role="option"
                aria-selected={isActive}
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => void handleSelect(suggestion)}
                  className={`block w-full px-3 py-2 text-left text-base text-text-primary transition-colors duration-micro ease-out ${isActive ? 'bg-hover-bg' : 'hover:bg-hover-bg'}`}
                >
                  {suggestion.placePrediction.text.toString()}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
